import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { authFetch } from "../api";
import Header from "../components/Header";
import NeighborhoodSelect from "../components/NeighborhoodSelect";

// ---- small presentational helpers -----------------------------------------

const card = {
  background: "#fff",
  border: "1px solid #e0e0e0",
  borderRadius: "8px",
  padding: "20px",
};

const sectionTitle = {
  fontSize: "13px",
  fontWeight: 700,
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  color: "#888",
  marginBottom: "14px",
};

function pct(n, d) {
  if (!d) return "0%";
  return `${((n / d) * 100).toFixed(1)}%`;
}

// A labeled count with an optional denominator/percentage.
function StatTile({ label, value, sub, color }) {
  return (
    <div style={{ ...card, flex: "1 1 160px", minWidth: 0 }}>
      <div style={{ fontSize: "28px", fontWeight: 700, color: color || "#222" }}>
        {value?.toLocaleString?.() ?? value}
      </div>
      <div style={{ fontSize: "13px", color: "#555", marginTop: "4px" }}>{label}</div>
      {sub != null && (
        <div style={{ fontSize: "12px", color: "#999", marginTop: "2px" }}>{sub}</div>
      )}
    </div>
  );
}

// Horizontal bar row for breakdowns (tier / type / floors).
function BarRow({ label, count, total, max, highlight }) {
  const widthPct = max ? (count / max) * 100 : 0;
  return (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "6px" }}>
      <div style={{ width: "140px", fontSize: "13px", color: highlight ? "#c62828" : "#333", fontWeight: highlight ? 700 : 400, flexShrink: 0, textAlign: "right" }}>
        {label}
      </div>
      <div style={{ flex: 1, background: "#f0f0f0", borderRadius: "4px", height: "20px", position: "relative", overflow: "hidden" }}>
        <div style={{ width: `${widthPct}%`, background: highlight ? "#ef9a9a" : "#90caf9", height: "100%" }} />
      </div>
      <div style={{ width: "110px", fontSize: "13px", color: "#333", flexShrink: 0 }}>
        {count.toLocaleString()} <span style={{ color: "#aaa" }}>({pct(count, total)})</span>
      </div>
    </div>
  );
}

function Breakdown({ title, rows, total, highlightFn }) {
  const max = rows.reduce((m, r) => Math.max(m, r.count), 0);
  return (
    <div style={card}>
      <div style={sectionTitle}>{title}</div>
      {rows.length === 0 && <div style={{ color: "#999", fontSize: "13px" }}>No data</div>}
      {rows.map((r) => (
        <BarRow
          key={r.label}
          label={r.label}
          count={r.count}
          total={total}
          max={max}
          highlight={highlightFn ? highlightFn(r) : false}
        />
      ))}
    </div>
  );
}

// Completeness bar (single metric, green fill).
function CoverageBar({ label, count, total }) {
  const p = total ? (count / total) * 100 : 0;
  const good = p >= 90;
  return (
    <div style={{ marginBottom: "14px" }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: "13px", marginBottom: "4px" }}>
        <span style={{ color: "#333" }}>{label}</span>
        <span style={{ color: "#666" }}>
          {count.toLocaleString()} / {total.toLocaleString()} ({pct(count, total)})
        </span>
      </div>
      <div style={{ background: "#f0f0f0", borderRadius: "4px", height: "10px", overflow: "hidden" }}>
        <div style={{ width: `${p}%`, background: good ? "#66bb6a" : p >= 70 ? "#ffb74d" : "#ef5350", height: "100%" }} />
      </div>
    </div>
  );
}

// ---- page ------------------------------------------------------------------

export default function BuildingStatsPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    if (!localStorage.getItem("token")) navigate("/login");
  }, [navigate]);

  const [neighborhoods, setNeighborhoods] = useState([]);
  const [hoodId, setHoodId] = useState(searchParams.get("neighborhood_id") || "");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    authFetch("/buildings/neighborhoods")
      .then((res) => {
        const hoods = (res.neighborhoods || []).sort((a, b) => a.name.localeCompare(b.name));
        setNeighborhoods(hoods);
      })
      .catch((e) => console.error("Error loading neighborhoods:", e));
  }, []);

  useEffect(() => {
    if (!hoodId) {
      setData(null);
      return;
    }
    setLoading(true);
    setError(null);
    setSearchParams({ neighborhood_id: String(hoodId) }, { replace: true });
    authFetch(`/buildings/stats?neighborhood_id=${hoodId}`)
      .then((res) => setData(res))
      .catch((e) => setError(e.message || "Failed to load stats"))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hoodId]);

  const v = data?.verified;
  const c = data?.completeness;
  const activeAnomalies = (data?.anomalies || []).filter((a) => a.count > 0);

  return (
    <>
      <Header showLogout={true} />
      <div style={{ padding: "40px", maxWidth: "1200px", margin: "0 auto" }}>
        <Link to="/buildings" style={{ color: "#1976d2", textDecoration: "none", fontSize: "14px" }}>
          ← Buildings
        </Link>
        <h1 style={{ margin: "10px 0 6px" }}>Building Stats &amp; Coverage</h1>
        <p style={{ color: "#666", marginBottom: "24px" }}>
          Per-neighborhood building coverage and data-quality checks. Pick a neighborhood to begin.
        </p>

        <div style={{ maxWidth: "360px", marginBottom: "28px" }}>
          <NeighborhoodSelect
            neighborhoods={neighborhoods}
            selected={hoodId}
            onChange={(id) => setHoodId(id)}
            multi={false}
            label="Neighborhood"
            placeholder="Select a neighborhood"
          />
        </div>

        {loading && <div style={{ color: "#666" }}>Loading…</div>}
        {error && (
          <div style={{ ...card, borderColor: "#ef9a9a", background: "#ffebee", color: "#c62828" }}>
            {error}
          </div>
        )}

        {!loading && !error && data && (
          <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>
            {/* Headline tiles */}
            <div style={{ display: "flex", flexWrap: "wrap", gap: "16px" }}>
              <StatTile label="Total buildings" value={data.total_buildings} />
              <StatTile
                label="Verified"
                value={v.verified}
                sub={pct(v.verified, data.total_buildings) + " of total"}
                color="#2e7d32"
              />
              <StatTile
                label="Human-verified"
                value={v.human}
                sub={pct(v.human, v.verified || 1) + " of verified"}
                color="#1565c0"
              />
              <StatTile
                label="Auto-verified"
                value={v.auto}
                sub={pct(v.auto, v.verified || 1) + " of verified"}
                color="#6a1b9a"
              />
              <StatTile
                label="Unverified"
                value={v.unverified}
                sub={pct(v.unverified, data.total_buildings) + " of total"}
                color={v.unverified > 0 ? "#c62828" : "#222"}
              />
            </div>

            {/* Anomaly panel */}
            <div style={{ ...card, borderColor: activeAnomalies.length ? "#ffcc80" : "#c8e6c9", background: activeAnomalies.length ? "#fff8e1" : "#f1f8e9" }}>
              <div style={sectionTitle}>⚠ Suspicious / data-quality checks</div>
              {activeAnomalies.length === 0 ? (
                <div style={{ fontSize: "13px", color: "#2e7d32" }}>
                  ✓ No anomalies detected — all checks clean for this neighborhood.
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: "8px" }}>
                  {data.anomalies.map((a) => (
                    <div
                      key={a.key}
                      style={{
                        display: "flex", justifyContent: "space-between", alignItems: "center",
                        padding: "8px 12px", borderRadius: "6px",
                        background: a.count > 0 ? "#fff" : "transparent",
                        border: a.count > 0 ? "1px solid #ffe0b2" : "1px solid transparent",
                        opacity: a.count > 0 ? 1 : 0.5,
                      }}
                    >
                      <span style={{ fontSize: "13px", color: "#333" }}>{a.label}</span>
                      <span style={{
                        fontSize: "14px", fontWeight: 700,
                        color: a.count > 0 ? "#e65100" : "#999",
                      }}>
                        {a.count.toLocaleString()}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Completeness + Listings linkage */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "24px" }}>
              <div style={card}>
                <div style={sectionTitle}>Data completeness</div>
                <CoverageBar label="Has building tier" count={c.has_tier} total={c.total} />
                <CoverageBar label="Has floor count" count={c.has_floors} total={c.total} />
                <CoverageBar label="Has year built" count={c.has_year_built} total={c.total} />
                <CoverageBar label="Has SE amenities scraped" count={c.has_se_amenities} total={c.total} />
              </div>

              <div style={card}>
                <div style={sectionTitle}>Listings linkage</div>
                <CoverageBar label="Buildings with an active listing" count={data.listings.with_active} total={data.listings.total} />
                <CoverageBar label="Buildings with any listing (ever)" count={data.listings.with_any} total={data.listings.total} />
                <div style={{ marginTop: "16px", fontSize: "13px", color: data.listings.without_any > 0 ? "#e65100" : "#666" }}>
                  <strong>{data.listings.without_any.toLocaleString()}</strong> buildings have no listing at all
                  {data.listings.without_any > 0 && " — possible orphans."}
                </div>
              </div>
            </div>

            {/* Breakdowns */}
            <Breakdown
              title="By tier"
              rows={data.tiers}
              total={data.total_buildings}
              highlightFn={(r) => r.label === "(none)"}
            />
            <Breakdown
              title="By ownership type"
              rows={data.types}
              total={data.total_buildings}
              highlightFn={(r) => r.label === "(none)"}
            />
            <Breakdown
              title="By floor count"
              rows={data.floors}
              total={data.total_buildings}
              highlightFn={(r) => r.label === "(unknown)" || r.label === "(invalid)"}
            />
          </div>
        )}
      </div>
    </>
  );
}
